import { useState } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Checkbox,
  Popover, PopoverContent, PopoverTrigger,
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@nexus/ui";
import { cn } from "@nexus/ui";

interface IndustryItem {
  index: string;
  title: string;
}

interface IndustryMultiSelectProps {
  selected: string[];
  onChange: (indices: string[]) => void;
  industries: IndustryItem[];
  disabled?: boolean;
  placeholder?: string;
  triggerClassName?: string;
}

export function IndustryMultiSelect({
  selected,
  onChange,
  industries,
  disabled = false,
  placeholder,
  triggerClassName,
}: IndustryMultiSelectProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const resolvedPlaceholder = placeholder ?? t("iam.shared.industryMultiSelect.placeholder", "Select industries…");
  const catalogIndices = new Set(industries.map((ind) => ind.index));

  const toggle = (index: string) => {
    if (selected.includes(index)) {
      onChange(selected.filter((i) => i !== index));
    } else {
      onChange([...selected, index]);
    }
  };

  const remove = (index: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(selected.filter((i) => i !== index));
  };

  const selectedItems = industries.filter((ind) => selected.includes(ind.index));
  const orphanIndices = selected.filter((index) => !catalogIndices.has(index));

  const triggerLabel = () => {
    if (selected.length === 0) return null;
    if (selected.length === 1) return selectedItems[0]?.title ?? selected[0];
    return `${selected.length} industries`;
  };

  const label = triggerLabel();

  return (
    <div className="flex flex-col gap-1.5">
      <Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            aria-expanded={open}
            aria-haspopup="listbox"
            className={cn(
              "flex h-9 w-full items-center justify-between gap-2 rounded-md border border-border bg-background px-3 text-sm outline-none transition-colors",
              "focus:border-ring focus:ring-1 focus:ring-ring/40",
              "disabled:cursor-not-allowed disabled:opacity-50",
              !label && "text-muted-foreground",
              triggerClassName,
            )}
          >
            <span className="truncate">{label ?? resolvedPlaceholder}</span>
            <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-50" aria-hidden />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={4}
          className="w-(--radix-popover-trigger-width) p-0"
        >
          <Command>
            <CommandInput placeholder="Search industries…" />
            <CommandList>
              <CommandEmpty>No industries found.</CommandEmpty>
              <CommandGroup>
                {industries.map((ind) => {
                  const isSelected = selected.includes(ind.index);
                  return (
                    <CommandItem
                      key={ind.index}
                      value={ind.index}
                      onSelect={() => toggle(ind.index)}
                      aria-selected={isSelected}
                    >
                      <Checkbox
                        checked={isSelected}
                        className="mr-2 shrink-0"
                        aria-hidden
                      />
                      <span className="flex-1 truncate">{ind.title}</span>
                      {isSelected && (
                        <Check className="ml-auto h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden />
                      )}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
            <div className="flex items-center justify-between gap-2 border-t border-border px-3 py-1.5 text-[11px]">
              <span className="text-muted-foreground">
                {t("iam.shared.industryMultiSelect.selectedCount", "{{count}} selected", { count: selected.length })}
              </span>
            </div>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Selected chips — include catalog matches and legacy indices missing from the catalog */}
      {(selectedItems.length > 0 || orphanIndices.length > 0) && (
        <div className="flex flex-wrap gap-1">
          {selectedItems.map((ind) => (
            <span
              key={ind.index}
              className="flex items-center gap-1 rounded-md border border-border bg-muted px-2 py-0.5 text-[11px] font-medium text-foreground"
            >
              {ind.title}
              {!disabled && (
                <button
                  type="button"
                  aria-label={t("iam.shared.industryMultiSelect.removeIndustry", "Remove {{name}}", { name: ind.title })}
                  onClick={(e) => remove(ind.index, e)}
                  className="ml-0.5 rounded hover:text-destructive focus:outline-none"
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              )}
            </span>
          ))}
          {orphanIndices.map((index) => (
            <span
              key={index}
              className="flex items-center gap-1 rounded-md border border-warning-subtle/40 bg-warning-subtle/10 px-2 py-0.5 text-[11px] font-medium text-foreground"
            >
              {index}
              {!disabled && (
                <button
                  type="button"
                  aria-label={t("iam.shared.industryMultiSelect.removeIndustry", "Remove {{name}}", { name: index })}
                  onClick={(e) => remove(index, e)}
                  className="ml-0.5 rounded hover:text-destructive focus:outline-none"
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
